<template>
  <div>
    <el-row style="margin-bottom: 20px;">
      <el-col :span="24">
        <el-button-group>
          <el-button
            type="info"
            icon="el-icon-back"
            size="mini"
            @click="$router.push({path: `/app/tenants/${$route.params.id}/customers/${$route.params.customerId}/accounts`})">
            {{ $t('settings.backToAccounts') }}
          </el-button>
          <el-button
            type="danger"
            icon="el-icon-delete"
            size="mini"
            @click="deleteAccount">
            {{ $t('settings.deleteAccount') }}
          </el-button>
        </el-button-group>
      </el-col>
    </el-row>

    <h2 v-if="customer && account">
      {{ $t('settings.editAccount') }} - {{ account.firstName }} {{ account.lastName }} ({{ customer.name }})
    </h2>
    <h2 v-else>{{ $t('settings.editAccount') }}</h2>

    <account-form
      v-if="account"
      :tenantId="$route.params.id"
      :customerId="$route.params.customerId"
      :account="account"
      :isEdit="true"
      @submit="onSubmit"
      @cancel="onCancel"
      :loading="loading">
    </account-form>

    <div v-if="account" class="account-actions">
      <h3>{{ $t('settings.accountActions') }}</h3>
      <el-row :gutter="20">
        <el-col :xs="24" :sm="8">
          <el-card class="action-card">
            <div slot="header">
              <span>{{ $t('settings.account.sendEmail') }}</span>
            </div>
            <el-button
              :disabled="!account.email"
              type="primary"
              @click="sendTestEmail"
              :loading="emailLoading">
              {{ $t('settings.account.sendTestEmail') }}
            </el-button>
          </el-card>
        </el-col>
        <el-col :xs="24" :sm="8">
          <el-card class="action-card">
            <div slot="header">
              <span>{{ $t('settings.account.sendPush') }}</span>
            </div>
            <el-button
              :disabled="!account.hasPushToken"
              type="primary"
              @click="sendTestPush"
              :loading="pushLoading">
              {{ $t('settings.account.sendTestPush') }}
            </el-button>
          </el-card>
        </el-col>
        <el-col :xs="24" :sm="8">
          <el-card class="action-card">
            <div slot="header">
              <span>{{ $t('settings.account.resetPushToken') }}</span>
            </div>
            <el-button
              :disabled="!account.hasPushToken"
              type="warning"
              @click="resetPushToken"
              :loading="tokenLoading">
              {{ $t('settings.account.resetAllTokens') }}
            </el-button>
          </el-card>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import AccountForm from './AccountForm';
import {adminAccountService} from "@/services/AdminAccountService.class";
import {customerService} from "@/services/CustomerService.class";

export default {
  name: "EditTenantCustomerAccount",
  components: {
    AccountForm
  },
  data() {
    return {
      customer: null,
      account: null,
      loading: false,
      emailLoading: false,
      pushLoading: false,
      tokenLoading: false
    };
  },
  mounted() {
    this.loadCustomer();
    this.loadAccount();
  },
  methods: {
    loadCustomer() {
      customerService.getById(this.$route.params.customerId)
        .then(response => {
          this.customer = response.data;
        })
        .catch(() => {
          this.$message.error(this.$t('settings.genericHttpError'));
        });
    },
    loadAccount() {
      adminAccountService.getAccountById(this.$route.params.accountId)
        .then(response => {
          this.account = response.data;
        })
        .catch(() => {
          this.$message.error(this.$t('settings.genericHttpError'));
          this.$router.push({
            path: `/app/tenants/${this.$route.params.id}/customers/${this.$route.params.customerId}/accounts`
          });
        });
    },
    onSubmit(account) {
      this.loading = true;
      adminAccountService.updateAccount(account.id, account)
        .then(() => {
          this.$message.success(this.$t('settings.accountUpdated'));
          this.loadAccount(); // Refresh account data
        })
        .catch(() => {
          this.$message.error(this.$t('settings.genericHttpError'));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onCancel() {
      this.$router.push({
        path: `/app/tenants/${this.$route.params.id}/customers/${this.$route.params.customerId}/accounts`
      });
    },
    deleteAccount() {
      this.$confirm(this.$t('settings.deleteAccountInfoText'), {
        confirmButtonClass: 'el-button--danger',
        confirmButtonText: this.$t('settings.deleteAccount'),
        cancelButtonText: this.$t('base.cancel'),
        type: 'warning'
      }).then(() => {
        this.loading = true;
        adminAccountService.deleteAccount(this.account.id)
          .then(() => {
            this.$message.success(this.$t('settings.accountDeleted'));
            this.$router.push({
              path: `/app/tenants/${this.$route.params.id}/customers/${this.$route.params.customerId}/accounts`
            });
          })
          .catch(() => {
            this.$message.error(this.$t('settings.genericHttpError'));
            this.loading = false;
          });
      });
    },
    sendTestEmail() {
      this.emailLoading = true;
      adminAccountService.sendTestEmail(this.account.id)
        .then(() => {
          this.$message.success(this.$t('settings.testEmailSent'));
        })
        .catch(() => {
          this.$message.error(this.$t('settings.testEmailFailed'));
        })
        .finally(() => {
          this.emailLoading = false;
        });
    },
    sendTestPush() {
      this.pushLoading = true;
      adminAccountService.sendTestPush(this.account.id)
        .then(() => {
          this.$message.success(this.$t('settings.testPushSent'));
        })
        .catch(() => {
          this.$message.error(this.$t('settings.testPushFailed'));
        })
        .finally(() => {
          this.pushLoading = false;
        });
    },
    resetPushToken() {
      this.tokenLoading = true;
      adminAccountService.deleteAllDeviceTokens(this.account.id)
        .then(() => {
          this.$message.success(this.$t('settings.pushTokensReset'));
          this.loadAccount(); // Refresh account data
        })
        .catch(() => {
          this.$message.error(this.$t('settings.pushTokensResetFailed'));
        })
        .finally(() => {
          this.tokenLoading = false;
        });
    }
  }
}
</script>

<style scoped lang="scss">
.account-actions {
  margin-top: 30px;

  .action-card {
    margin-bottom: 20px;
    text-align: center;

    .el-button {
      margin: 10px 0;
      width: 100%;
    }
  }
}
</style>
